import { supabase } from '@/lib/supabase';
import type { CommunityChannelRow } from '@/types';
import type { Channel } from '../community-view/channels';

/**
 * Community channels — real `community_channels` rows (they used to live in
 * localStorage, one browser away from deletion). Reads go through the
 * `ensure_community_channels` RPC, which lazily seeds the default rail for
 * communities created before the table existed; writes are the owner-only
 * atomic `replace_community_channels`.
 */

export function rowToChannel(row: CommunityChannelRow): Channel {
  return {
    id: row.id,
    name: row.name,
    kind: row.kind === 'voice' ? 'voice' : 'text',
    section: row.section,
    ...(row.topic ? { topic: row.topic } : {}),
    ...(row.read_only ? { readOnly: true } : {}),
    ...(row.icon ? { icon: row.icon } : {}),
    ...(row.effect ? { effect: row.effect } : {}),
  };
}

export async function ensureCommunityChannels(communityId: string): Promise<Channel[]> {
  const { data, error } = await supabase.rpc('ensure_community_channels', {
    p_community_id: communityId,
  });
  if (error) throw error;
  return (data ?? []).map(rowToChannel);
}

export async function replaceCommunityChannels(
  communityId: string,
  channels: Channel[],
): Promise<Channel[]> {
  const { data, error } = await supabase.rpc('replace_community_channels', {
    p_community_id: communityId,
    p_channels: channels.map((ch) => ({
      id: ch.id,
      name: ch.name,
      kind: ch.kind,
      section: ch.section,
      icon: ch.icon ?? null,
      effect: ch.effect ?? null,
      topic: ch.topic ?? null,
      readOnly: ch.readOnly ?? false,
    })),
  });
  if (error) throw error;
  return (data ?? []).map(rowToChannel);
}
